import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmpInfo, getSchoolYear } from '../../Api'
import { statusDiarioTurma, carregarPeriodoAtual } from '../../utils/diaryStatus'
import { DiaryStatus, DiaryLegend } from '../../components/DiaryStatus'

import {
    Container,
    ContainerDivs,
    Header,
    HeaderInfo,
    Title,
    Subtitle,
    CountPill,
    Search,
    FormSearch,
    FormFilter,
    SearchInputWrap,
    InputEmp,
    Select,
    //Hint,
    List,
    Emp,
    EmpMain,
    Span,
    SerieTag,
    ShiftTag,
    GoArrow,
    EmptyState,
    RetryButton,
    //SkeletonCard,
    Overlay,
    BlockModal,
    LogoutButton,
} from './style';
import LoadingSpinner from '../../components/Loading'

import { FiSearch } from 'react-icons/fi'

const normalize = (str = '') =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

const MyCla$$ = () => {

    const navigate = useNavigate()

    const [Clss, setClss] = useState([])
    const [filter, setFilter] = useState('')
    const [busca, setBusca] = useState('')
    const [blocked, setBlocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [regime, setRegime] = useState('');
    const [periodoAtual, setPeriodoAtual] = useState(1);
    const [idTeacher, setIdTeacher] = useState('');

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        (async () => {
            setLoading(true);
            setError(false);
            try {
                const idSchool = sessionStorage.getItem("id-school");
                const id_employee = localStorage.getItem("Id_employee");

                // duas chamadas independentes em paralelo
                const [schoolYear, res] = await Promise.all([
                    getSchoolYear(JSON.parse(idSchool)),
                    EmpInfo(JSON.parse(id_employee)),
                ]);

                if (!mountedRef.current) return;

                const schoolYearValue = schoolYear.data.data;
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth(); // 0-11

                if (Number(schoolYearValue) !== currentYear && currentMonth > 1) {
                    setBlocked(true);
                }

                const employee = res.data.data.find(r => r);
                const yearStr = JSON.stringify(schoolYearValue);

                const classes = (employee?.id_class || [])
                    .filter(c => c && c.year === yearStr);

                // limpezas de sessão (sem custo de rede)
                sessionStorage.removeItem("classRegentTeacher");
                sessionStorage.removeItem("classRegentTeacher02");
                sessionStorage.removeItem("physicalEducationTeacher");
                sessionStorage.removeItem("class-info");

                setClss(classes);
                setIdTeacher(String(JSON.parse(id_employee) || ''));

                // regime + período atual (para a cor do selo do diário)
                try {
                    const info = await carregarPeriodoAtual(schoolYearValue, JSON.parse(idSchool));
                    if (mountedRef.current) {
                        setRegime(info.regime);
                        setPeriodoAtual(info.periodoAtual);
                    }
                } catch (e) {
                    console.error("Erro ao carregar períodos:", e);
                }
            } catch (err) {
                console.error("Erro ao carregar turmas:", err);
                if (mountedRef.current) setError(true);
            } finally {
                if (mountedRef.current) setLoading(false);
            }
        })();

        return () => { mountedRef.current = false; };
    }, [reloadKey]);

    // dedup + filtro de turno + busca (nome/série) — memoizado
    const visibleClasses = useMemo(() => {
        const seen = new Set();
        const unique = Clss.filter(c => {
            if (!c || seen.has(c._id)) return false;
            seen.add(c._id);
            return true;
        });

        const q = normalize(busca.trim());

        return unique.filter(c => {
            if (filter && c.shift !== filter) return false;
            if (!q) return true;
            const name = normalize(c.name);
            const serie = normalize(c.serie);
            return name.includes(q) || serie.includes(q);
        });
    }, [Clss, filter, busca]);

    const MyClassInformation = (clss) => {
        if (!clss) return;
        setLoading(true);
        const id_teacher = JSON.parse(localStorage.getItem("Id_employee"))
        sessionStorage.setItem("class-info", clss._id)
        navigate(`/myclassesinfo/${clss._id}/${id_teacher}`)
        setLoading(false);
    }

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();
        navigate("/");
    };

    const hasAnyClass = Clss.length > 0;

    // Quais diários da turma são deste professor (regente e/ou Ed. Física)
    const chavesDoProfessor = (clss) => {
        const tem = (lista) => (lista || []).some(t => String(t?._id || t) === idTeacher);
        const chaves = [];
        if (tem(clss.classRegentTeacher) || tem(clss.classRegentTeacher02)) chaves.push('regentTeacher');
        if (tem(clss.physicalEducationTeacher)) chaves.push('physicalEducationTeacher');
        return chaves.length ? chaves : null; // null = considera todos
    };

    return (
        <Container>
            {loading ? (
                <LoadingSpinner />
            ) : (
                <ContainerDivs>
                    <Header>
                        <HeaderInfo>
                            <Title>🚀 Minhas Turmas</Title>
                            <Subtitle>Clique em uma turma para acessá-la</Subtitle>
                        </HeaderInfo>
                        {hasAnyClass && (
                            <CountPill>
                                <strong>{visibleClasses.length}</strong>
                                <span>{visibleClasses.length === 1 ? 'Turma' : 'Turmas'}</span>
                            </CountPill>
                        )}
                    </Header>

                    {error ? (
                        <EmptyState>
                            <span className="icon">⚠️</span>
                            <h3>Não foi possível carregar as turmas</h3>
                            <p>Verifique sua conexão e tente novamente.</p>
                            <RetryButton onClick={() => setReloadKey(k => k + 1)}>
                                Tentar novamente
                            </RetryButton>
                        </EmptyState>
                    ) : (
                        <>
                            <Search>
                                <FormSearch>
                                    <label>Buscar turma</label>
                                    <SearchInputWrap>
                                        <FiSearch />
                                        <InputEmp
                                            type="text"
                                            placeholder="Buscar por nome ou série"
                                            value={busca}
                                            onChange={(e) => setBusca(e.target.value)}
                                        />
                                    </SearchInputWrap>
                                </FormSearch>
                                <FormFilter>
                                    <label>Filtrar por turno</label>
                                    <Select
                                        id="position"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="MATUTINO">Matutino</option>
                                        <option value="VESPERTINO">Vespertino</option>
                                        <option value="NOTURNO">Noturno</option>
                                    </Select>
                                </FormFilter>
                            </Search>

                            {visibleClasses.length > 0 && <DiaryLegend />}

                            {visibleClasses.length > 0 ? (
                                <List>
                                    {visibleClasses.map((clss, i) => (
                                        <Emp
                                            key={clss._id}
                                            onClick={() => MyClassInformation(clss)}
                                            style={{ animationDelay: `${Math.min(i * 0.03, 0.4)}s` }}
                                        >
                                            <EmpMain>
                                                <Span>{clss.name}</Span>
                                                {clss.serie && <SerieTag>{clss.serie}</SerieTag>}
                                            </EmpMain>
                                            {clss.shift && <ShiftTag $shift={clss.shift}>{clss.shift}</ShiftTag>}
                                            {clss.dailyStatus && (
                                                <DiaryStatus
                                                    {...statusDiarioTurma(clss.dailyStatus, regime, periodoAtual, chavesDoProfessor(clss))}
                                                />
                                            )}
                                            <GoArrow>›</GoArrow>
                                        </Emp>
                                    ))}
                                </List>
                            ) : (
                                <EmptyState>
                                    <span className="icon">{hasAnyClass ? '🔍' : '📭'}</span>
                                    <h3>
                                        {hasAnyClass
                                            ? 'Nenhuma turma encontrada'
                                            : 'Nenhuma turma atribuída'}
                                    </h3>
                                    <p>
                                        {hasAnyClass
                                            ? 'Tente ajustar a busca ou o filtro de turno.'
                                            : 'Você ainda não possui turmas neste ano letivo.'}
                                    </p>
                                </EmptyState>
                            )}
                        </>
                    )}

                    {blocked && (
                        <Overlay>
                            <BlockModal>
                                <h3>⚠ Diário indisponível</h3>
                                <p>
                                    O diário está indisponível no momento.
                                    <br /><br />
                                    Para mais informações, entre em contato com a supervisão ou direção da escola.
                                </p>
                                <LogoutButton onClick={handleLogout}>Sair</LogoutButton>
                            </BlockModal>
                        </Overlay>
                    )}
                </ContainerDivs>
            )}
        </Container>
    )
}

export default MyCla$$